// #6750 — What to do with the search terms a reader has ticked.
//
// Three actions, each taking a match type: promote the term into the campaign it
// came from, exclude it from that campaign, or put it on one of the account's
// shared negative lists. The dialog asks only what the action cannot assume —
// the match type always, and which list when the target is a list — because
// everything else (which terms, which campaigns) is already on screen.
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { NegativeKeywordList } from '@/lib/types';

/** BROAD, PHRASE, EXACT. EXACT first because a search term is a literal query. */
export const MATCH_TYPES = ['EXACT', 'PHRASE', 'BROAD'] as const;
export type MatchType = (typeof MATCH_TYPES)[number];

export type SearchTermAction = 'keyword' | 'negative' | 'list';

const TITLE: Record<SearchTermAction, string> = {
  keyword: 'Add as keyword',
  negative: 'Add as negative',
  list: 'Add to a negative list',
};

// What the action does, in the reader's terms. The difference between the last
// two is the whole reason both exist, so it is spelt out rather than implied.
const EXPLANATION: Record<SearchTermAction, string> = {
  keyword: 'The term is added to the ad it served under, in the campaign it came from.',
  negative: 'The term is excluded from the campaign it came from. Other campaigns can still serve it.',
  list: 'The term is added to a shared list, so it is excluded from every campaign that list applies to.',
};

const matchTypeLabel = (mt: string) => mt.charAt(0) + mt.slice(1).toLowerCase();

export function SearchTermActionDialog({
  action,
  termCount,
  lists,
  listsLoading,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  /** null closes the dialog. */
  action: SearchTermAction | null;
  termCount: number;
  lists: NegativeKeywordList[];
  listsLoading: boolean;
  busy: boolean;
  /** Shown inline; never a toast for something the reader is looking at. */
  error: string | null;
  onConfirm: (choice: { matchType: MatchType; listId?: string }) => void;
  onCancel: () => void;
}) {
  const [matchType, setMatchType] = useState<MatchType>('EXACT');
  const [listId, setListId] = useState<string>('');

  // A fresh ask every time: the match type from the last action is not a
  // preference, and carrying it over would hide a choice being made.
  useEffect(() => {
    if (action) {
      setMatchType('EXACT');
      setListId('');
    }
  }, [action]);

  if (!action) return null;

  const needsList = action === 'list';
  const noLists = needsList && !listsLoading && lists.length === 0;
  // Tell-on-click would need somewhere to tell; with one required choice the
  // button simply waits for it.
  const canConfirm = !busy && (!needsList || !!listId);

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {TITLE[action]}: {termCount.toLocaleString()} search term{termCount === 1 ? '' : 's'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-500">{EXPLANATION[action]}</p>

          <div className="space-y-2">
            <Label htmlFor="search-term-match-type">Match type</Label>
            <Select value={matchType} onValueChange={(v) => setMatchType(v as MatchType)} disabled={busy}>
              <SelectTrigger id="search-term-match-type" className="h-9 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MATCH_TYPES.map((mt) => (
                  <SelectItem key={mt} value={mt}>
                    {matchTypeLabel(mt)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Exact by default: a search term is a literal query somebody typed, not a theme.
            </p>
          </div>

          {needsList && (
            <div className="space-y-2">
              <Label htmlFor="search-term-list">List</Label>
              {noLists ? (
                <p className="text-sm text-gray-500">
                  There are no negative keyword lists on this account yet. Create one under Assets first.
                </p>
              ) : (
                <Select value={listId} onValueChange={setListId} disabled={busy || listsLoading}>
                  <SelectTrigger id="search-term-list" className="h-9 bg-white">
                    <SelectValue placeholder={listsLoading ? 'Loading lists...' : 'Choose a list'} />
                  </SelectTrigger>
                  <SelectContent>
                    {lists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {error && (
            <div className="border-l-4 border-[#FCA5A5] bg-[#FEE2E2] px-3 py-2 text-sm text-[#B91C1C]">{error}</div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm({ matchType, listId: needsList ? listId : undefined })}
            disabled={!canConfirm || noLists}
            className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {busy ? 'Adding...' : TITLE[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
